import React from "react";
import { useGame, useContacts } from "@seabrookstudios/pitch2table";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CardHeader from "@mui/material/CardHeader";
import { UserName, useContactName } from "../users/UserName";

/**
 * @param {import("@seabrookstudios/pitch2table-core").GameDesigners[]} designers
 */
export const useDesigners = (designers = []) => {
  const userIds = designers.map(({ designerId }) => designerId);
  return useContacts(userIds);
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTest[]} playTests
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
export const useFormatPlayers = (playTests = [], gameId) => {
  const { data: game } = useGame(gameId);
  const { data: designers = [] } = useDesigners(game ? game.designers : []);

  const outcomes = playTests.map((pt) => pt.outcomes || []).flat();

  if (outcomes.length === 0) {
    return "No results";
  }

  const anon = outcomes.filter((o) => o.anonymous);
  const ai = outcomes.filter((o) => o.ai);

  const players = outcomes
    .filter((o) => !o.anonymous && !o.ai)
    .map((o) => {
      if (o.user && o.userId) {
        const designer = designers.find((d) => d.id === o.userId);
        return designer ? `${designer.firstName} ${designer.lastName}` : o.name;
      }
      return o.name;
    })
    .reduce((acc, p) => (acc.includes(p) ? acc : [...acc, p]), /** @type {string[]} */ ([]))
    .sort();

  const anonGroup = anon.length > 0 ? `${anon.length} anonymous` : undefined;
  const aiGroup = ai.length > 0 ? `${ai.length} AI` : undefined;

  const groups = [...players, anonGroup, aiGroup].filter((g) => g);

  if (groups.length === 1) {
    return `${groups[0] ?? ""}`;
  }

  const head = groups.slice(0, -1).join(", ");
  const tail = groups.slice(-1)[0];

  return `${head} & ${tail}`;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTest} props.pt
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 */
export const FormatPlayers = ({ pt, gameId }) => {
  const { data: game } = useGame(gameId);
  const { data: designers = [] } = useDesigners(game ? game.designers : []);

  if (pt.outcomes.length === 0) {
    return (
      <Box display="flex" flexDirection="row">
        <Typography>No results</Typography>
      </Box>
    );
  }

  const anon = pt.outcomes.filter((o) => o.anonymous);
  const ai = pt.outcomes.filter((o) => o.ai);

  const players = pt.outcomes
    .filter((o) => !o.anonymous && !o.ai)
    .map((o) => {
      if (o.user && o.userId) {
        const designer = designers.find((d) => d.id === o.userId);
        return designer ? `${designer.firstName} ${designer.lastName}` : o.name;
      }
      return o.name;
    });

  const anonGroup = anon.length > 0 ? `${anon.length} anonymous` : undefined;
  const aiGroup = ai.length > 0 ? `${ai.length} AI` : undefined;

  const groups = [...players, anonGroup, aiGroup].filter((g) => g);

  if (groups.length === 1) {
    return (
      <Box display="flex" flexDirection="row">
        <Typography component="span">{groups[0] ?? ""}</Typography>
      </Box>
    );
  }

  const head = groups.slice(0, -1).map((p, i, arr) => (
    <Typography key={p} component="span">
      {p}
      {i < arr.length - 1 && <Typography component="span">,&nbsp;</Typography>}
    </Typography>
  ));
  const tail = groups.slice(-1)[0];

  return (
    <Box display="flex" flexDirection="row">
      {head}
      <Typography component="span">&nbsp;&&nbsp;</Typography>
      <Typography component="span">{tail}</Typography>
    </Box>
  );
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestPlayer} player
 * @returns {string}
 */
export const usePlayerNameString = (player) => {
  const contactName = useContactName(player.userId);

  if (player.anonymous) {
    return "Anonymous";
  }

  if (player.ai) {
    return "AI";
  }

  if (player.user && player.userId) {
    return contactName || player.name || "";
  }

  return player.name || "";
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestPlayer} props.player
 */
export const PlayerName = ({ player }) => {
  if (player.anonymous) {
    return <Typography variant="inherit">Anonymous</Typography>;
  }

  if (player.ai) {
    return <Typography variant="inherit">AI</Typography>;
  }

  if (player.user && player.userId) {
    return <UserName userId={player.userId} />;
  }

  return <Typography variant="inherit">{player.name}</Typography>;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestPlayer} props.player
 */
export const PlayerNameSubheader = ({ player }) => {
  const playerName = usePlayerNameString(player);

  return <CardHeader subheader={`Score breakdown for ${playerName}`} />;
};
